/** `useState`-shaped hook persisted to browser localStorage (pool-app port; used only for cosmetic prefs like location). */
import { useCallback, useState, type Dispatch, type SetStateAction } from 'react';

export function useLocalStorage<T>(
  key: string,
  initialValue: T,
): [T, Dispatch<SetStateAction<T>>] {
  const [stored, setStored] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item !== null ? (JSON.parse(item) as T) : initialValue;
    } catch {
      return initialValue;
    }
  });

  const setValue: Dispatch<SetStateAction<T>> = useCallback(
    (action) => {
      setStored((prev) => {
        const next =
          typeof action === 'function' ? (action as (p: T) => T)(prev) : action;
        try {
          window.localStorage.setItem(key, JSON.stringify(next));
        } catch {
          // Quota / private-mode failures are non-fatal for a cosmetic pref.
        }
        return next;
      });
    },
    [key],
  );

  return [stored, setValue];
}
